<template>
  <div class="relative" v-bind="$attrs">
    <ul
      ref="listEl"
      class="mx-2 2xs:portrait:simulfest:mx-0 flex flex-col sm:min-w-72 gap-1.5 simulfest:portrait:gap[1cqw] max-h-[40vh] overflow-y-auto hide-scrollbar"
    >
      <li
        v-for="rank in props.ranking"
        :key="rank.user.id"
        class="flex flex-row gap-2 simulfest:portrait:gap-[2.3cqw] simulfest:xs-mobile:gap-4 rounded-full p-1.5 pr-3 items-center justify-left hover:bg-fill-brand-secondary hover:cursor-pointer"
        :class="[rank.user.id === userStore.userId && 'bg-surface']"
        :title="rank.user.username"
        @click="onUserClick(rank.user.id)"
      >
        <div
          class="simulfest:landscape:w-[5.8cqh] simulfest:landscape:h-[5.8cqh] simulfest:landscape:text-[4cqh] simulfest:portrait:w-[9.1cqw] simulfest:portrait:h-[9.1cqw] simulfest:portrait:text-[4cqw] px-1.5 flex items-center justify-center text-center shrink-0 border-2 rounded-full font-serif [box-shadow:0px_3.594px_3.594px_0px_rgba(0,_0,_0,_0.25)_inset] text-text"
          :class="[
            rank.position === 1
              ? 'bg-gradient-to-bl from-[#ffca17] to-[#967400]'
              : rank.user.id === userStore.userId
                ? 'bg-fill-brand-secondary border-border-brand'
                : 'bg-surface border-border-brand',
            props.variant === 'larger-text'
              ? 'text-xl h-40u min-w-40u '
              : 'text-base h-28u min-w-28u ',
          ]"
        >
          {{ rank.position }}
        </div>

        <Avatar
          :size="
            isSimulfest
              ? 'responsive'
              : props.variant === 'larger-text'
                ? 'lg'
                : 'sm'
          "
          :user="rank.user"
          class="simulfest:portrait:w-[9.1cqw] simulfest:portrait:h-[9.1cqw] simulfest:landscape:size-[5.8cqh] aspect-square rounded-full"
        />

        <span
          class="flex-1 items-center flex leading-none gap-2 simulfest:landscape:text-[2.6cqh] simulfest:portrait:text-[4cqw]"
          :class="[props.variant === 'larger-text' ? 'text-xl ' : 'text-sm ']"
        >
          <UsernameWithBadge
            :username="rank.user.username"
            :is-vip="rank.is_superfan ?? rank.has_paid_membership ?? false"
            text-class="simulfest:landscape:text-[2.6cqh] simulfest:portrait:text-[4cqw]"
            class="py-0.5"
          />
          <CountryBadge
            class="shrink-0 simulfest:landscape:size-[2.6cqh] simulfest:portrait:size-[4cqw]"
            :icon="rank.user.country_iso"
          />
          <template v-if="rank.user.communities?.length">
            <img
              v-for="c in rank.user.communities.slice(0, 3)"
              :key="c.community?.id"
              :src="c.community?.image ?? undefined"
              :alt="c.community?.name"
              class="shrink-0 size-4 simulfest:landscape:size-[2.6cqh] simulfest:portrait:size-[4cqw] rounded-full"
            />
          </template>
        </span>

        <span
          class="font-bold flex items-center leading-none simulfest:landscape:text-[2.6cqh] simulfest:portrait:text-[4cqw]"
          :class="[
            rank.position === 1
              ? 'text-text-gold'
              : rank.user.id === userStore.userId && 'text-brand-primary',
            props.variant === 'larger-text' ? 'text-2xl ' : 'text-base ',
          ]"
        >
          {{ rank.points }}
        </span>
      </li>

      <!-- Sentinel Element -->
      <li ref="infiniteScrollTrigger" class="h-4 min-h-4"></li>
    </ul>

    <Spinner v-if="loading" class="bg-transparent" />
  </div>

  <ActivityModal
    v-model:is-open="isActivityModalOpen"
    :loading="userActivityLoading"
    :user="selectedRanking?.user ?? null"
    :is-vip="
      selectedRanking?.is_superfan ??
      selectedRanking?.has_paid_membership ??
      false
    "
    :user-activities-with-summary="userActivitiesWithSummary"
    @close="isActivityModalOpen = false"
  />
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";

export const ContestLeaderboardUserFragment = gql(/* GraphQL */ `
  fragment ContestLeaderboardUser on LeaderboardUser {
    points
    position
    has_paid_membership
    is_superfan
    user {
      id
      username
      avatar
      country_iso
      communities {
        community {
          id
          name
          image
        }
      }
    }
  }
`);
</script>

<script lang="ts" setup>
import Avatar from "@/components/me/avatar.vue";
import { useUserStore } from "@/stores/user.store";
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
  defineAsyncComponent,
} from "vue";
import { isSimulfestSession } from "@/utils/simulfest";
import Spinner from "@/components/ui/spinner.vue";
import { useQuery } from "@vue/apollo-composable";
import { useChannelStore } from "@/stores/channel.store";
import { GetUserActivitiesWithSummaryDocument } from "@/generated/graphql";
import CountryBadge from "@/components/badge/country-badge.vue";
import { getStartOfMonthWithOffset, startOfUTCWeek } from "@/utils/date";
import { LeaderboardLength } from "@f3/shared/leaderboard-types";
import { AppThemeKind, useThemeStore } from "@/stores/theme.store";
import type { ResultOf } from "@graphql-typed-document-node/core";
import UsernameWithBadge from "@/components/me/username-with-badge.vue";

// Lazy load
const ActivityModal = defineAsyncComponent(
  () => import("@/components/leaderboard/activity-modal.vue"),
);

const props = defineProps<{
  loading?: boolean;
  variant?: "larger-text" | "normal-text";
  ranking: ResultOf<typeof ContestLeaderboardUserFragment>[] | undefined;
  leaderboardLength?: LeaderboardLength;
}>();

const emit = defineEmits<{
  (e: "loadMore"): void;
}>();

const isActivityModalOpen = ref(false);
const selectedUserId = ref<string | null>(null);

const userStore = useUserStore();
const channelStore = useChannelStore();
const themeStore = useThemeStore();
const isSimulfest = isSimulfestSession();

const onUserClick = (userId: string) => {
  isActivityModalOpen.value = true;
  selectedUserId.value = userId;
};

const {
  result: getUserActivitiesWithSummaryResult,
  loading: userActivityLoading,
} = useQuery(
  GetUserActivitiesWithSummaryDocument,
  computed(() => ({
    channel_id: channelStore.channelSlug!,
    user_id: selectedUserId.value!,
    date:
      props.leaderboardLength === LeaderboardLength.Weekly
        ? startOfUTCWeek(new Date()).toISOString()
        : getStartOfMonthWithOffset(channelStore.channelSlug!).toISOString(),
    community_id:
      themeStore.currentTheme?.kind === AppThemeKind.community
        ? themeStore.currentTheme?.id
        : undefined,
  })),
  {
    enabled: computed(
      () => !!selectedUserId.value && !!channelStore.channelSlug,
    ),
  },
);
const userActivitiesWithSummary = computed(
  () => getUserActivitiesWithSummaryResult.value?.userActivitiesWithSummary,
);

const selectedRanking = computed(() =>
  props.ranking?.find((rank) => rank.user.id === selectedUserId.value),
);

const infiniteScrollTrigger = useTemplateRef("infiniteScrollTrigger");
const listEl = useTemplateRef<HTMLElement>("listEl");
let observer: IntersectionObserver | undefined;

onMounted(() => {
  if (!infiniteScrollTrigger.value) return;
  // Observe against the scrolling <ul>, not the viewport. With `root: null`
  // the sentinel stays "intersecting" whenever the list fits the viewport
  // regardless of scroll position, which fires loadMore spuriously.
  observer = new IntersectionObserver(
    (entries) => {
      if (entries?.[0]?.isIntersecting) {
        emit("loadMore");
      }
    },
    {
      root: listEl.value ?? null,
      threshold: 0.1,
    },
  );
  observer.observe(infiniteScrollTrigger.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>
